import { useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import type { UseFormReturn } from "react-hook-form";

import type { ArtworkForm } from "@design-lab/shared/types";
import type { Tab, AppTab, SurfaceMetaSides } from "@design-lab/shared/types/tab";
import { useAppTabs, APP_TABS_BASE } from "@design-lab/shared/ui/layouts/state";
import { useResponsive } from "./useResponsive";
import { useVisitedTabs } from "./useVisitedTabs";
import { useIsSample } from "./useIsSample";
import { useDesignSubTabInitialization } from "./useDesignSubTabInitialization";

type UseTabNavigationProps = {
  artworkForm?: UseFormReturn<ArtworkForm> | null;
  sides?: SurfaceMetaSides;
};

type UseTabNavigationReturn = {
  tab: Tab;
  setTab: (newTab: Tab) => void;
  currentIndex: number;
  currentTab: AppTab | undefined;
  nextTab: AppTab | undefined;
  nextTabRaw: AppTab | undefined;
  prevTab: AppTab | undefined;
  prevTabRaw: AppTab | undefined;
  canBeSkipped: boolean;
  canGoNext: boolean;
  canGoBack: boolean;
  back: () => void;
  isDesignTab: boolean;
  isProductsTab: boolean;
  isSizeTab: boolean;
  isSample: boolean;
};

export function useTabNavigation(props: UseTabNavigationProps = {}): UseTabNavigationReturn {
  const { artworkForm = null, sides } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [, setVisitedTabs] = useVisitedTabs();
  const isSample = useIsSample();
  const { isDesktop } = useResponsive();
  const appTabsRaw = useAppTabs();
  const appTabs = appTabsRaw && appTabsRaw.length > 0 ? appTabsRaw : APP_TABS_BASE;
  const tabFromURL = searchParams.get("tab") as Tab | undefined;

  useDesignSubTabInitialization({ artworkForm });

  const fallbackTab = useMemo(() => {
    const uploadArtworkUrls = searchParams.get("upload_urls");
    let initialTab: Tab = "upload";

    // Cart edit links may use ?tab=front or ?tab=back — redirect to "design"
    if (tabFromURL === "front" || tabFromURL === "back") {
      return "design";
    }

    if (!tabFromURL && uploadArtworkUrls) {
      try {
        const urlsObj = JSON.parse(uploadArtworkUrls) as Record<string, string>;
        if (urlsObj?.["front"]) {
          initialTab = "front";
        } else if (urlsObj?.["back"]) {
          initialTab = "back";
        }
      } catch {
        // ignore malformed upload_urls and use default tab
      }
    }

    // "front" and "back" tabs don't exist as top-level tabs -- redirect to "design"
    if (initialTab === "front" || initialTab === "back") {
      initialTab = "design";
    }

    return initialTab;
  }, [tabFromURL, searchParams]);

  // Ensure the tab exists in the current appTabs list, otherwise use fallback
  let tab: Tab;
  if (appTabs.some((t: AppTab) => t.value === tabFromURL)) {
    tab = tabFromURL!;
  } else if (appTabs.some((t: AppTab) => t.value === fallbackTab)) {
    tab = fallbackTab;
  } else {
    tab = appTabs[0]?.value ?? "upload";
  }

  useEffect(() => {
    setVisitedTabs((prev: Set<Tab>) => new Set(prev).add(tab));
  }, [setVisitedTabs, tab]);

  const hasSetInitialTabRef = useRef(false);

  useEffect(() => {
    // Redirect legacy front/back tab params to "design"
    if (tabFromURL === "front" || tabFromURL === "back") {
      const next = new URLSearchParams(searchParams);
      next.set("tab", "design");
      setSearchParams(next, { replace: true });
      return;
    }

    if (tabFromURL) {
      hasSetInitialTabRef.current = false;
      return;
    }

    if (hasSetInitialTabRef.current) return;

    const currentTabInURL = searchParams.get("tab");
    if (currentTabInURL === fallbackTab) {
      hasSetInitialTabRef.current = true;
      return;
    }

    hasSetInitialTabRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.set("tab", fallbackTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromURL, fallbackTab]);

  const setTab = useCallback(
    (newTab: Tab) => {
      if (newTab === tab) return;
      const next = new URLSearchParams(searchParams);
      next.set("tab", newTab);
      setSearchParams(next);
    },
    [tab, searchParams, setSearchParams]
  );

  const currentIndex = appTabs.findIndex((t: AppTab) => t.value === tab);
  const currentTab = appTabs[currentIndex];
  const nextTabRaw = appTabs[(currentIndex + 1) % appTabs.length];
  const prevTabRaw = currentIndex > 0 ? appTabs[currentIndex - 1] : undefined;

  const nextTab = nextTabRaw
    ? {
        ...nextTabRaw,
        title: isSample && nextTabRaw.value === "products" ? "Colors" : nextTabRaw.title,
        mobileTitle: isSample && nextTabRaw.value === "products" ? "Colors" : nextTabRaw.mobileTitle
      }
    : undefined;

  const prevTab = prevTabRaw
    ? {
        ...prevTabRaw,
        title: isSample && prevTabRaw.value === "products" ? "Colors" : prevTabRaw.title,
        mobileTitle: isSample && prevTabRaw.value === "products" ? "Colors" : prevTabRaw.mobileTitle
      }
    : undefined;

  const canBeSkipped = Boolean(currentTab?.canBeSkipped?.({ sides, isDesktop }));
  const canGoNext = currentIndex < appTabs.length - 1;
  const canGoBack = currentIndex > 0;

  const isDesignTab = useMemo(() => tab === "design", [tab]);
  const isProductsTab = useMemo(() => tab === "products", [tab]);
  const isSizeTab = useMemo(() => tab === "size", [tab]);

  const back = useCallback(() => {
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
    }
  }, [prevTabRaw, setTab]);

  return {
    tab,
    setTab,
    currentIndex,
    currentTab,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    back,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  };
}
